import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { TRACKING_TYPES, TRACKING_STATUS } from "@/lib/constants";
import { Loader2, Plus, Trash2, CheckCircle2, ChevronLeft, ChevronRight, CalendarRange } from "lucide-react";

const ACCENTS = {
  emerald: "border-emerald-500",
  blue: "border-blue-600",
  slate: "border-slate-300",
};
const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const typeMeta = (id) => TRACKING_TYPES.find((t) => t.id === id) || TRACKING_TYPES[0];
const monthName = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
};

export default function TrackingCard({ studentId, readOnly }) {
  const { user } = useAuth();
  const role = user?.role;
  const canEdit = !readOnly && (role === "admin" || role === "coach");
  const canApprove = role === "admin";
  const canMark = role === "student";

  const [items, setItems] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));
  const [type, setType] = useState("ozel-ders");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tracking-cards/${studentId}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (canEdit) api.get("/coaches").then((r) => setCoaches(r.data)).catch(() => {});
  }, [canEdit]);
  useEffect(() => { setForm({}); }, [type]);

  const active = typeMeta(type);
  const setF = (n, v) => setForm((f) => ({ ...f, [n]: v }));

  const save = async () => {
    if (!form.date) return toast.error(`${active.dateLabel} zorunlu`);
    setBusy(true);
    try {
      const payload = { type, date: form.date, status: "Bekliyor" };
      for (const f of active.fields) {
        if (f.type === "number") payload[f.name] = Number(form[f.name] || 0);
        else payload[f.name] = form[f.name] || "";
        if (f.type === "coach") {
          const c = coaches.find((x) => x.id === form[f.name]);
          payload.teacher_name = c ? c.name : "";
        }
      }
      await api.post(`/tracking-cards/${studentId}`, payload);
      toast.success("Takip kartı eklendi");
      setForm({});
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (item, status) => {
    try {
      await api.put(`/tracking-cards/${studentId}/${item.id}`, { ...item, status });
      toast.success(status === "Onaylı" ? "Onaylandı" : "Güncellendi");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/tracking-cards/${studentId}/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const shiftMonth = (d) => {
    const [y, m] = ym.split("-").map(Number);
    const nd = new Date(y, m - 1 + d, 1);
    setYm(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}`);
  };

  const [year, month] = ym.split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const cellsFor = (day) => {
    const key = `${ym}-${String(day).padStart(2, "0")}`;
    return items.filter((i) => (i.date || "").slice(0, 10) === key);
  };

  return (
    <div className="space-y-6" data-testid="tracking-card">
      {/* ZOR AKADEMİ styled header */}
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 text-center">
        <div className="text-[11px] tracking-[0.3em] text-slate-400 font-semibold">ZOR AKADEMİ · 2019</div>
        <h3 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 mt-1">TAKİP KARTI {year}</h3>
        <div className="text-[11px] tracking-[0.25em] text-slate-400 font-semibold mt-1">NATIONAL ACADEMY</div>
      </div>

      {/* Add form (admin/coach) */}
      {canEdit && (
        <div className="card-ta p-5 space-y-4" data-testid="tracking-add">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {TRACKING_TYPES.map((t) => (
              <button
                key={t.id}
                onClick={() => setType(t.id)}
                data-testid={`tracking-type-${t.id}`}
                className={`relative text-left rounded-xl border-2 p-3 transition-all ${ACCENTS[t.accent]} ${type === t.id ? "ring-2 ring-offset-2 ring-slate-900 bg-slate-50" : "bg-white hover:bg-slate-50"}`}
              >
                <span className="absolute top-2 right-3 text-2xl font-display font-black text-slate-200">{t.no}</span>
                <div className="text-xs font-bold uppercase tracking-wide text-slate-700 pr-6">{t.label}</div>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="ta-label">{active.dateLabel}</label>
              <input type="date" className="ta-input" value={form.date || ""} onChange={(e) => setF("date", e.target.value)} data-testid="tracking-date" />
            </div>
            {active.fields.map((f) => (
              <div key={f.name}>
                <label className="ta-label">{f.label}</label>
                {f.type === "coach" ? (
                  <select className="ta-input" value={form[f.name] || ""} onChange={(e) => setF(f.name, e.target.value)} data-testid={`tracking-${f.name}`}>
                    <option value="">Hoca seç</option>
                    {coaches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <input type={f.type} className="ta-input" placeholder={f.type === "number" ? "0" : ""} value={form[f.name] || ""} onChange={(e) => setF(f.name, e.target.value)} data-testid={`tracking-${f.name}`} />
                )}
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={busy} className="bg-indigo-900 hover:bg-indigo-800" data-testid="tracking-save">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}<span className="ml-1">Karta Ekle</span>
          </Button>
        </div>
      )}

      {/* Monthly calendar */}
      <div className="card-ta p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-slate-700 font-semibold"><CalendarRange size={18} /> {monthName(ym)}</div>
          <div className="flex gap-1">
            <button onClick={() => shiftMonth(-1)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="tracking-prev-month"><ChevronLeft size={18} /></button>
            <button onClick={() => shiftMonth(1)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="tracking-next-month"><ChevronRight size={18} /></button>
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
        ) : (
          <div className="grid grid-cols-7 gap-1.5" data-testid="tracking-calendar">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center text-[11px] font-bold text-slate-400 uppercase py-1">{w}</div>
            ))}
            {Array.from({ length: firstWeekday }).map((_, i) => <div key={`b${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const cards = cellsFor(day);
              return (
                <div key={day} className="min-h-[92px] rounded-lg border border-slate-100 bg-slate-50/60 p-1.5" data-testid={`tracking-day-${day}`}>
                  <div className="text-[11px] font-bold text-slate-400">{day}</div>
                  <div className="space-y-1 mt-0.5">
                    {cards.map((c) => {
                      const m = typeMeta(c.type);
                      const st = TRACKING_STATUS[c.status] || TRACKING_STATUS["Bekliyor"];
                      return (
                        <div key={c.id} className={`rounded-md border-l-2 ${ACCENTS[m.accent]} bg-white px-1.5 py-1 text-[10px] leading-tight`} data-testid={`tracking-item-${c.id}`}>
                          <div className="font-bold text-slate-700 truncate">{m.no}. {m.label.split(" ")[0]}</div>
                          <div className="text-slate-500 truncate">
                            {c.subject || c.topic || "-"}{c.teacher_name ? ` · ${c.teacher_name}` : ""}
                          </div>
                          <span className={`inline-block mt-0.5 px-1 rounded border text-[9px] ${st.cls}`}>{st.label}</span>
                          <div className="flex gap-1 mt-1">
                            {canMark && c.status === "Bekliyor" && (
                              <button onClick={() => setStatus(c, "Aldı")} className="text-[9px] text-blue-600 hover:underline" data-testid={`tracking-take-${c.id}`}>Aldım</button>
                            )}
                            {canApprove && c.status !== "Onaylı" && (
                              <button onClick={() => setStatus(c, "Onaylı")} className="text-[9px] text-emerald-600 hover:underline flex items-center gap-0.5" data-testid={`tracking-approve-${c.id}`}><CheckCircle2 size={10} /> Onayla</button>
                            )}
                            {canEdit && (
                              <button onClick={() => del(c.id)} className="text-[9px] text-slate-300 hover:text-rose-600 ml-auto" data-testid={`tracking-del-${c.id}`}><Trash2 size={10} /></button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
